import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import posthog from "posthog-js";

import { ENDPOINTS } from "@/api/application-api";
import { useAuth } from "@/context/auth-context";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { resolvePendingAuthStep } from "@/utils/postLoginNavigation";
import { displayErrorToast } from "@/utils/toast";
import { User } from "@/types/user";

export interface LoginRequest {
  email: string;
  password: string;
  recaptcha_response?: string;
}

export interface LoginResponse {
  access_token: string;
  user: User;
}

export const useLogin = (onError?: (verdict: string) => void) => {
  const auth = useAuth();
  const navigate = useNavigate();

  return useMutation(
    ["login"],
    async (loginRequest: LoginRequest): Promise<LoginResponse> => {
      const response = await fetch(ENDPOINTS.auth.login, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(loginRequest),
      });

      return handleResponse(response);
    },
    {
      onError: (error: string | { verdict: string }) => {
        if (onError && typeof error === "object" && "verdict" in error) {
          onError(error.verdict);
        } else {
          displayErrorToast(error);
        }
      },
      onSuccess: async ({ access_token, user }) => {
        auth?.login(access_token, user);
        posthog.capture("Logged In");

        const pendingStep = resolvePendingAuthStep(access_token, user);
        if (pendingStep) {
          navigate(pendingStep);
          localStorage.setItem("last_login_type", "email");
          return;
        }

        // Login payload omits memberships; /home loads /users/me and resolves the
        // real default workspace instead of falling back to the first workspace.
        navigate("/home");
        localStorage.setItem("last_login_type", "email");
      },
    },
  );
};
